import React, { useState, useEffect } from "react";
import { loadNewsArticles } from "../../../api/news";
import { truncateAtLastSpace } from "../../../utils/textUtils";
import NewsCardSkeleton from "./NewsCardSkeleton";
import ExternalArticleModal from "../articles/ExternalArticleModal";
import "../../../assets/css/animate.css";
import "./news.css";

const NewsPage = () => {
  const DISPLAY_WINDOW_DAYS = 7;
  const [newsArticles, setNewsArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("videos");
  const [playingVideoId, setPlayingVideoId] = useState("");
  const [isMobileViewport, setIsMobileViewport] = useState(window.innerWidth <= 768);
  const [externalModal, setExternalModal] = useState({ isOpen: false, url: "", title: "", mediaType: "article" });

  const getYouTubeVideoId = (url) => {
    if (!url) return "";

    try {
      const parsed = new URL(url);
      const host = parsed.hostname.toLowerCase().replace(/^www\./, "");

      if (host === "youtu.be") {
        return parsed.pathname.replace(/^\//, "");
      }

      if (host.endsWith("youtube.com")) {
        if (parsed.pathname === "/watch") {
          return parsed.searchParams.get("v") || "";
        }
        if (parsed.pathname.startsWith("/shorts/") || parsed.pathname.startsWith("/embed/")) {
          return parsed.pathname.split("/")[2] || "";
        }
      }

      return "";
    } catch (error) {
      return "";
    }
  };

  const parseArticleDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? null : date;
    } catch (error) {
      return null;
    }
  };

  const getYouTubeThumbnailUrl = (url) => {
    const videoId = getYouTubeVideoId(url);
    if (!videoId) return "";

    // Use broadly available variants to avoid maxres 404 noise in console.
    return isMobileViewport
      ? `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`
      : `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoId = getYouTubeVideoId(url);

      if (!videoId) return "";

      const embedUrl = new URL(`https://www.youtube.com/embed/${videoId}`);
      embedUrl.searchParams.set("autoplay", "1");
        embedUrl.searchParams.set("controls", "1");
        embedUrl.searchParams.set("playsinline", "1");
      embedUrl.searchParams.set("rel", "0");
      embedUrl.searchParams.set("modestbranding", "1");
      return embedUrl.toString();
  };

  useEffect(() => {
    const loadData = async () => {
      try {
      setLoading(true);
      console.log('Loading all articles'); // Debug log
      
      const articles = await loadNewsArticles(30, true, 250);
      
      console.log('Loaded articles:', articles.length); // Debug log
      
      // Filter articles/videos to only include those from the last week.
      const displayWindowStart = new Date();
      displayWindowStart.setDate(displayWindowStart.getDate() - DISPLAY_WINDOW_DAYS);
      
      const filteredArticles = articles.filter(article => {
        const articleDate = parseArticleDate(article['Publication Date']);
        return articleDate && articleDate >= displayWindowStart;
      });
      
      console.log(`Filtered articles (last ${DISPLAY_WINDOW_DAYS} days):`, filteredArticles.length); // Debug log
      
        // Sort articles by publication date (newest first)
      const sortedArticles = filteredArticles.sort((a, b) => {
        const dateA = parseArticleDate(a['Publication Date']);
        const dateB = parseArticleDate(b['Publication Date']);
        
        // Handle invalid dates by putting them at the end
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        // Sort newest first (descending order)
        return dateB.getTime() - dateA.getTime();
      });
      
      console.log('Setting articles:', sortedArticles.length); // Debug log
      setNewsArticles(sortedArticles);
    } catch (error) {
      console.error('Failed to load news articles:', error);
      setNewsArticles([]);
    } finally {
      setLoading(false);
    }
    };

    window.scrollTo(0, 0);
    loadData();
  }, [DISPLAY_WINDOW_DAYS]);

  useEffect(() => {
    if (activeTab !== "videos") {
      setPlayingVideoId("");
    }
  }, [activeTab]);

  useEffect(() => {
    const onResize = () => {
      setIsMobileViewport(window.innerWidth <= 768);
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const getBackgroundColor = (index) => {
    const colors = [
      'rgba(47, 172, 102, 0.85)',   // vert
      'rgba(206, 20, 70, 0.85)',    // rouge
      'rgba(47, 137, 252, 0.85)',   // bleu
      'rgba(255, 153, 0, 0.85)',    // orange
      'rgba(156, 39, 176, 0.85)',   // violet
      'rgba(0, 150, 136, 0.85)',    // turquoise
      'rgba(233, 30, 99, 0.85)',    // rose
      'rgba(255, 193, 7, 0.85)',    // jaune
      'rgba(103, 58, 183, 0.85)',   // indigo
      'rgba(96, 125, 139, 0.85)'    // gris bleuté
    ];
    return colors[index % 10];
  };

  const getSourceColor = (source) => {
    const sourceColors = {
      "AJ+ français": "#d81b60",
      "Ajib.fr": "#1565c0",
      "Al-Kanz": "#6d4c41",
      "CFCM": "#00897b",
      "Mizane.info": "#2e7d32",
      "MizaneTV": "#ef6c00",
      "Musulmans en France": "#3949ab",
      "Nouvelle Aube": "#f9a825",
      "Orient XXI": "#00838f",
      "Oumma": "#8e24aa",
      "SaphirNews": "#455a64",
    };

    return sourceColors[source] || "#546e7a";
  };

  const openExternalNews = (article) => {
    if (!article?.Link) return;

    setExternalModal({
      isOpen: true,
      url: article.Link,
      title: getDisplayTitle(article),
      mediaType: article.mediaType || "article",
    });
  };

  const closeExternalNews = () => {
    setExternalModal({ isOpen: false, url: "", title: "", mediaType: "article" });
  };

  const isVideoArticle = (article) => (article?.mediaType || "article") === "video";

  const getDisplayDescription = (article) => {
    const description = typeof article?.Description === "string" ? article.Description.trim() : "";
    if (!description || description.toUpperCase() === "N/A") return "";
    return description;
  };

  const getDisplayTitle = (article) => {
    const rawTitle = typeof article?.Title === "string" ? article.Title.trim() : "";
    const normalizedTitle = rawTitle.toUpperCase();

    if (rawTitle && normalizedTitle !== "N/A" && normalizedTitle !== "NA") {
      return rawTitle;
    }

    const description = getDisplayDescription(article);
    if (description) {
      return description.length > 110 ? `${description.slice(0, 107).trimEnd()}...` : description;
    }

    return "Actualite sans titre";
  };

  const formatInlinePublicationDate = (dateStr) => {
    const date = parseArticleDate(dateStr);
    if (!date) return "";

    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getReadMoreText = (article) => (isVideoArticle(article) ? "Voir la vidéo" : "Lire l'article");

  const articlesList = newsArticles.filter((a) => !isVideoArticle(a));
  const videosList = newsArticles.filter((a) => isVideoArticle(a));
  const displayedArticles = activeTab === "videos" ? videosList : articlesList;
  const hasVideos = videosList.length > 0;

  const renderCard = (article, index) => {
    const isVideo = isVideoArticle(article);
    const thumbnailUrl = isVideo ? getYouTubeThumbnailUrl(article.Link) : "";
    const embedUrl = isVideo ? getYouTubeEmbedUrl(article.Link) : "";
    const isPlaying = isVideo && playingVideoId === article.id && embedUrl;
    const description = getDisplayDescription(article);
    const displayTitle = getDisplayTitle(article);
    const publicationDateLabel = formatInlinePublicationDate(article['Publication Date']);

    if (isVideo) {
      return (
        <div
          key={article.id}
          className="col-lg-4 col-md-6 d-flex wow fadeInUp animated"
          data-wow-delay="200ms"
          data-wow-duration="1500ms"
        >
          <div className="news-card-md news-card-video w-100">
            <div className="news-thumb news-thumb-video">
              {isPlaying ? (
                <iframe
                  title={displayTitle || "Vidéo YouTube"}
                  src={embedUrl}
                  className="news-video-embed"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <button
                  type="button"
                  className="news-video-surface"
                  onClick={() => setPlayingVideoId(article.id)}
                  aria-label={`Lire la vidéo ${displayTitle || "YouTube"}`}
                >
                  {thumbnailUrl ? (
                    <div className="news-video-thumb-wrap">
                      <img
                        src={thumbnailUrl}
                        alt={displayTitle || "Vidéo YouTube"}
                        className="news-video-thumb"
                        loading="lazy"
                      />
                      <div className="news-video-play-badge">
                        <i className="bi bi-play-fill"></i>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="news-image-placeholder"
                      style={{ background: getBackgroundColor(index) }}
                    >
                      <i className="bi bi-play-circle"></i>
                    </div>
                  )}
                </button>
              )}
            </div>
            <div className="news-content">
              <div className="news-source-date-row">
                <div className="news-source-inline" style={{ background: getSourceColor(article.Source) }}>
                  <i className="bi bi-rss"></i>
                  <span>{article.Source}</span>
                </div>
                {publicationDateLabel && <span className="news-date-inline news-date-inline-right">{publicationDateLabel}</span>}
              </div>
              <div className="news-title-row">
                <h4 className="news-title-below">{truncateAtLastSpace(displayTitle, 100)}</h4>
              </div>
              {description && (
                <p className="news-description">{truncateAtLastSpace(description, 150)}</p>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        key={article.id}
        className="col-lg-4 col-md-6 d-flex wow fadeInUp animated"
        data-wow-delay="200ms"
        data-wow-duration="1500ms"
      >
        <button
          type="button"
          onClick={() => openExternalNews(article)}
          className="news-card-link w-100"
          style={{ border: "none", background: "transparent", padding: 0, textAlign: "inherit" }}
        >
          <div className="news-card-md">
            <div className="news-thumb">
              <div
                className="news-image-placeholder"
                style={{ background: getBackgroundColor(index) }}
              >
                <i className="bi bi-newspaper"></i>
              </div>
              <div className="news-info-container">
                <h4 className="news-title-centered">{truncateAtLastSpace(displayTitle, 80)}</h4>
              </div>
              <div
                className="news-source-block"
                style={{ background: getSourceColor(article.Source) }}
              >
                <div className="news-source-block-left">
                  <i className="bi bi-rss" />
                  <span>{article.Source}</span>
                </div>
                {publicationDateLabel && <span className="news-source-block-date">{publicationDateLabel}</span>}
              </div>
            </div>
            <div className="news-content">
              {description && (
                <p className="news-description">{truncateAtLastSpace(description, 150)}</p>
              )}
              <div className="news-footer">
                <span className="news-read-more">
                  {getReadMoreText(article)} <i className="bi bi-box-arrow-up-right"></i>
                </span>
              </div>
            </div>
          </div>
        </button>
      </div>
    );
  };

  return (
    <>
      <div className="blog-area gray-300">
        <div className="container position-relative pt-110">

          {loading ? (
            <>
              <div className="row"><div className="col-lg-12"><div className="section-head"><h3>Dernières actualités</h3></div></div></div>
              <div className="row">
                <div className="col-lg-12">
                  <div className="row news-grid gy-4">
                    {[...Array(6)].map((_, index) => (
                      <NewsCardSkeleton key={index} />
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
          <>
            <div className="row">
              <div className="col-lg-12">
                <div className="section-head">
                  <h3>Dernières actualités</h3>
                </div>
                <div className="news-tabs" role="tablist" aria-label="Filtrer actualités et vidéos">
                  <button
                    type="button"
                    className={`news-tab-btn ${activeTab === "videos" ? "active" : ""}`}
                    onClick={() => setActiveTab("videos")}
                    role="tab"
                    aria-selected={activeTab === "videos"}
                    disabled={!hasVideos}
                  >
                    Vidéos ({videosList.length})
                  </button>
                  <button
                    type="button"
                    className={`news-tab-btn ${activeTab === "articles" ? "active" : ""}`}
                    onClick={() => setActiveTab("articles")}
                    role="tab"
                    aria-selected={activeTab === "articles"}
                  >
                    Articles ({articlesList.length})
                  </button>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-12">
                {displayedArticles.length > 0 ? (
                  <div className="row news-grid gy-4">
                    {displayedArticles.map((article, index) => renderCard(article, index))}
                  </div>
                ) : (
                  <div className="text-center empty-news-state">
                    <h4>{activeTab === "videos" ? "Aucune vidéo trouvée" : "Aucun article trouvé"}</h4>
                    <p>
                      {activeTab === "videos"
                        ? "Aucune vidéo n'a été trouvée pour les derniers 15 jours."
                        : "Aucun article n'a été trouvé pour les derniers 15 jours."}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
          )}
      <ExternalArticleModal
        isOpen={externalModal.isOpen}
        url={externalModal.url}
        title={externalModal.title}
        mediaType={externalModal.mediaType}
        onClose={closeExternalNews}
      />
      </div>
    </div>
  </>
  );
};

export default NewsPage;
